import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'vtwo:data-[state=open]:animate-in vtwo:data-[state=closed]:animate-out vtwo:data-[state=closed]:fade-out-0 vtwo:data-[state=open]:fade-in-0 vtwo:fixed vtwo:inset-0 vtwo:z-50 vtwo:bg-black/50',
        className
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'vtwo:bg-background vtwo:data-[state=open]:animate-in vtwo:data-[state=closed]:animate-out vtwo:data-[state=closed]:fade-out-0 vtwo:data-[state=open]:fade-in-0 vtwo:data-[state=closed]:zoom-out-95 vtwo:data-[state=open]:zoom-in-95 vtwo:fixed vtwo:top-[50%] vtwo:left-[50%] vtwo:z-50 vtwo:grid vtwo:w-full vtwo:max-w-[calc(100%-2rem)] vtwo:translate-x-[-50%] vtwo:translate-y-[-50%] vtwo:gap-4 vtwo:rounded-lg vtwo:border vtwo:p-6 vtwo:shadow-lg vtwo:duration-200 vtwo:sm:max-w-lg',
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="vtwo:ring-offset-background vtwo:focus:ring-ring vtwo:data-[state=open]:bg-accent vtwo:data-[state=open]:text-muted-foreground vtwo:absolute vtwo:top-4 vtwo:right-4 vtwo:rounded-xs vtwo:opacity-70 vtwo:transition-opacity vtwo:hover:opacity-100 vtwo:focus:ring-2 vtwo:focus:ring-offset-2 vtwo:focus:outline-hidden vtwo:disabled:pointer-events-none vtwo:[&_svg]:pointer-events-none vtwo:[&_svg]:shrink-0 vtwo:[&_svg:not([class*=size-])]:size-4"
          >
            <XIcon />
            <span className="vtwo:sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'vtwo:flex vtwo:flex-col vtwo:gap-2 vtwo:text-center vtwo:sm:text-left',
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'vtwo:flex vtwo:flex-col-reverse vtwo:gap-2 vtwo:sm:flex-row vtwo:sm:justify-end',
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('vtwo:text-lg vtwo:leading-none vtwo:font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('vtwo:text-muted-foreground vtwo:text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
